import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { useHistory } from "react-router-dom";
import { SpaceCategory } from "@lifeat/shared/data-models/virtual-space/SpaceCategory";
import { VirtualSpace } from "@lifeat/shared/data-models/virtual-space/VirtualSpace";
import {
    SpaceSidebarTab,
    doesSpaceMatchTab,
    pickFirstSpaceToPlay,
    weightedSpaceShuffle,
} from "@lifeat/shared/spaces/SpacesUtil";
import { QueryParameters } from "@lifeat/shared/system/RouteConstants";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import GA4Event, { GA4EventProperties } from "@analytics/GoogleAnalytics/GA4Event";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import { GlobalAudioContextType, useGlobalAudioContext } from "@contexts/GlobalAudioContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { useEvent } from "@hooks/useEvent";
import { requestFunction } from "@service/ServerlessFunctionsService";
import { updateAmbianceVolumeSetting, updateSavedSpaces } from "@user/service/UserSettingsService";
import { RouteUtil } from "@util/index";
import { ChangeCategoryEvent, SaveSpaceEvent } from "./AmplitudeSpaceEvents";

const NULL_SPACE_ID = "-1";

const NULL_SPACE: VirtualSpace = {
    id: NULL_SPACE_ID,
    category: SpaceCategory.MYSTERY,
    name: "Loading...",
    videoUrl: "",
};

const REMOVED_SPACE_PLACEHOLDER = (removedId: string): VirtualSpace => {
    return {
        id: removedId,
        category: SpaceCategory.MYSTERY,
        name: "Removed Space",
        removed: true,
        videoUrl: "",
    };
};

const DEFAULT_VOL = 0.25;

export default function useSpacePlayerController(defaultSpaceId?: string) {
    const history = useHistory();

    const amplitudeService = AmplitudeService.getInstance();

    const { masterVolume }: GlobalAudioContextType = useGlobalAudioContext();
    const { user, userData }: UserContextType = useUserContext();

    const changeCategoryEventRef = useRef<ChangeCategoryEvent>(null);

    // **********************
    // * INDEPENDENT STATES *
    // **********************

    const [activeCategories, setActiveCategories] = useState<SpaceCategory[]>([SpaceCategory.NATURE]);
    const [allSpacesOrdered, setAllSpacesOrdered] = useState<VirtualSpace[]>([]);
    const [selectedTab, setActiveTab] = useState<SpaceSidebarTab>(SpaceSidebarTab.VIDEOS);
    const [selectedCategory, setSelectedCategory] = useState<SpaceCategory | null>(null);
    const [selectedSpace, setSelectedSpace] = useState<VirtualSpace>(NULL_SPACE);
    const [savedSpaces, setSavedSpaces] = useState<VirtualSpace[]>([]);
    const [volume, setVolume] = useState<number>(0);
    const [autoplayedVolume, setAutoplayedVolume] = useState(false);
    const [lastViewedIdByCategory, setLastViewedIdByCategory] = useState<Map<SpaceCategory, string>>(new Map());

    const isVisibleOnSidebar = useCallback(
        (space: VirtualSpace) => {
            return !space.removed && doesSpaceMatchTab(space, selectedTab);
        },
        [selectedTab],
    );

    // ******************
    // * DERIVED STATES *
    // ******************

    const spacesById = useMemo(
        () => Object.fromEntries(allSpacesOrdered.map((space) => [space.id, space])),
        [allSpacesOrdered],
    );
    const spacesByCategory = useMemo(
        () =>
            allSpacesOrdered.filter(isVisibleOnSidebar).reduce((acc, space) => {
                const categorySpaces = acc.get(space.category);
                if (categorySpaces == null) {
                    acc.set(space.category, [space]);
                } else {
                    categorySpaces.push(space);
                }
                return acc;
            }, new Map<SpaceCategory, VirtualSpace[]>()),
        [allSpacesOrdered, isVisibleOnSidebar],
    );
    const featuredSpaces = useMemo(
        () => allSpacesOrdered.filter(isVisibleOnSidebar).filter((space) => space.featured),
        [allSpacesOrdered, isVisibleOnSidebar],
    );
    const recommendedSpaces = useMemo(
        () => allSpacesOrdered.filter(isVisibleOnSidebar).slice(0, 10),
        [allSpacesOrdered, isVisibleOnSidebar],
    );

    // *********************
    // * UTILITY FUNCTIONS *
    // *********************

    /**
     * Handles logic for autoplaying sound when the user changes space for first time
     */
    function autoplayVolume() {
        if (!autoplayedVolume) {
            if (masterVolume !== 0 && volume === 0) {
                setVolume(DEFAULT_VOL);
            }
            setAutoplayedVolume(true); //toggle autoPlayed indicator so that the volume only auto-plays on first shuffle and never after
        }
    }

    const muteForMobile = () => {
        // if the space changes and user is on mobile device, need to mute
        // the video so that autoplay can still work.
        if (isMobile || isTablet) {
            setVolume(0);
        }
    };

    /**
     * Handles logic for logging a ChangeCategoryEvent
     * @param space
     *     The new VirtualSpace context
     * @param forceLog
     *     Forces data to be logged despite typical logging logic
     */
    function logChangeCategoryEvent(space: VirtualSpace, forceLog?: boolean) {
        const event = changeCategoryEventRef.current;
        const updatedEvent = new ChangeCategoryEvent(space.category, Date.now());

        if (!event) {
            // Space player initialized
            changeCategoryEventRef.current = updatedEvent;
        } else if (forceLog || event.props.category !== space.category) {
            // Category changed
            autoplayVolume();

            // Log event for Amplitude, so that it tracks how much time was spent in the category
            // before changing -> tells us popularity of categories.
            event.props.duration = Date.now() - event.props.startTime;
            amplitudeService.logEvent(event);
            changeCategoryEventRef.current = updatedEvent;

            GA4Service.logSpaceEvent(GA4Event.SWITCH_CATEGORY, {
                [GA4EventProperties.space_info_keys.category]: space.category,
            });
        }
    }

    const logSaveSpaceEvent = useEvent((space: VirtualSpace) => {
        GA4Service.logSpaceEvent(GA4Event.SAVE_SPACE, {
            [GA4EventProperties.space_info_keys.id]: space.id,
            [GA4EventProperties.space_info_keys.name]: space.name,
            [GA4EventProperties.space_info_keys.category]: space.category,
            [GA4EventProperties.space_creator_name.key]: space.creatorName,
        });
        amplitudeService.logEvent(
            new SaveSpaceEvent({
                spaceId: space.id,
                spaceName: space.name,
                spaceCategory: space.category,
                spaceCreator: space.creatorName,
            }),
        );
    });

    const logShuffleSpaceEvent = useEvent((space: VirtualSpace, category: SpaceCategory) => {
        // NOTE: do not log for Amplitude due to volume of this event
        GA4Service.logSpaceEvent(GA4Event.SHUFFLE_SPACE, {
            [GA4EventProperties.space_info_keys.id]: space.id,
            [GA4EventProperties.space_info_keys.name]: space.name,
            [GA4EventProperties.space_info_keys.category]: category,
        });
    });

    function getNextSpaceForCategory(category: SpaceCategory): VirtualSpace {
        muteForMobile();
        const currCategorySpaces: VirtualSpace[] = spacesByCategory.get(category);
        let space: VirtualSpace = NULL_SPACE;
        if (currCategorySpaces && currCategorySpaces.length > 0) {
            const lastViewedId = lastViewedIdByCategory.get(category);
            let currCategorySpaceIdx = spacesByCategory.get(category).findIndex((space) => space.id === lastViewedId);
            const nextSpaceIdx =
                currCategorySpaceIdx < 0 || currCategorySpaceIdx >= currCategorySpaces.length - 1
                    ? 0
                    : currCategorySpaceIdx + 1;

            // Update the tracking map so each category has the last visited space
            space = currCategorySpaces[nextSpaceIdx];
            setLastViewedIdByCategory((original) => {
                const next = new Map(original);
                next.set(category, space.id);
                return next;
            });
        }
        return space;
    }

    function getPrevSpaceForCategory(category: SpaceCategory): VirtualSpace {
        muteForMobile();
        const currCategorySpaces: VirtualSpace[] = spacesByCategory.get(category);

        let space: VirtualSpace = NULL_SPACE;
        if (currCategorySpaces && currCategorySpaces.length > 0) {
            const lastViewedId = lastViewedIdByCategory.get(category);
            let currCategorySpaceIdx = spacesByCategory.get(category).findIndex((space) => space.id === lastViewedId);
            const prevSpaceIdx =
                currCategorySpaceIdx < 1 || currCategorySpaceIdx > currCategorySpaces.length - 1
                    ? currCategorySpaces.length - 1
                    : currCategorySpaceIdx - 1;

            // Update the tracking map so each category has the last visited space
            space = currCategorySpaces[prevSpaceIdx];
            setLastViewedIdByCategory((original) => {
                const next = new Map(original);
                next.set(category, space.id);
                return next;
            });
        }

        return space;
    }

    // ****************************
    // * SPACE CONTROLLER METHODS *
    // ****************************

    const getSpaceById = useCallback(
        (spaceId: string): VirtualSpace => {
            const space = spacesById[spaceId];
            return space ?? REMOVED_SPACE_PLACEHOLDER(spaceId);
        },
        [spacesById],
    );

    const searchSpacesByText = useCallback(
        (searchTerm: string) =>
            allSpacesOrdered.filter(isVisibleOnSidebar).filter((space) => {
                const normalizedSearchTerm = searchTerm.toLowerCase();
                return (
                    space.name.toLowerCase().includes(normalizedSearchTerm) ||
                    space.category.toLowerCase().includes(normalizedSearchTerm) ||
                    !!space.creatorName?.toLowerCase().includes(normalizedSearchTerm)
                );
            }),
        [allSpacesOrdered, isVisibleOnSidebar],
    );

    const toggleSpaceSaved = useEvent((save: boolean, target: VirtualSpace) => {
        if (save) {
            const next = [...savedSpaces];
            next.unshift(target); // add to the front of the list so it shows on top
            updateSavedSpaces(
                next.map((space) => space.id),
                user,
            );
            setSavedSpaces(next);

            // Log events for saving a space
            logSaveSpaceEvent(target);
        } else {
            // removing a saved space
            const next = savedSpaces.filter((space) => space.id !== target.id);
            updateSavedSpaces(
                next.map((space) => space.id),
                user,
            );
            setSavedSpaces(next);
        }
    });

    const playSpace = useEvent((space: VirtualSpace) => {
        setSelectedSpace(space);
        history.replace({ search: RouteUtil.updateQueryParams(QueryParameters.Space, space.id) });
    });

    const selectCategory = useEvent((category: SpaceCategory) => {
        setSelectedCategory(category);
        if (selectedSpace.category !== category) {
            const nextSpace = getNextSpaceForCategory(category);
            setSelectedSpace(nextSpace);

            logChangeCategoryEvent(selectedSpace);
        }
    });

    const playNextSpaceInCategory = useEvent((category: SpaceCategory) => {
        muteForMobile();
        const nextSpace = getNextSpaceForCategory(category);
        setSelectedSpace(nextSpace);
        setSelectedCategory(category);

        autoplayVolume();

        logShuffleSpaceEvent(nextSpace, category);
    });

    const playPreviousSpaceInCategory = useEvent((category: SpaceCategory) => {
        muteForMobile();
        const nextSpace = getPrevSpaceForCategory(category);
        setSelectedSpace(nextSpace);
        setSelectedCategory(category);

        autoplayVolume();

        logShuffleSpaceEvent(nextSpace, category);
    });

    const updateVolume = useEvent((newVol: number) => {
        setVolume(newVol);
        updateAmbianceVolumeSetting(newVol, user);
    });

    const isSpaceSaved = useCallback(
        (space: VirtualSpace) => {
            return savedSpaces?.some((saved) => saved.id === space.id) ?? false;
        },
        [savedSpaces],
    );

    // *******************
    // * EFFECT HANDLERS *
    // *******************

    const initializeSavedSpaces = useEvent(() => {
        const savedSpaceIds = userData?.savedSpaces;
        if (savedSpaceIds && allSpacesOrdered.length > 0) {
            setSavedSpaces(
                savedSpaceIds.map((id: string) => {
                    if (spacesById[id]) {
                        return spacesById[id];
                    } else {
                        return REMOVED_SPACE_PLACEHOLDER(id);
                    }
                }),
            );
        }
    });

    const onMount = useEvent(async () => {
        let data;
        if (process.env.GET_SPACES_CDN_URL) {
            data = await (await fetch(process.env.GET_SPACES_CDN_URL)).json();
        } else {
            data = await requestFunction("SpacesFunctions-getSpaces");
        }

        setAllSpacesOrdered(weightedSpaceShuffle(data.spaces));
        setActiveCategories(data.activeCategories);
    });

    // ***********
    // * EFFECTS *
    // ***********

    useEffect(() => {
        onMount();
    }, [onMount]);
    useEffect(() => {
        initializeSavedSpaces();
    }, [userData, allSpacesOrdered, initializeSavedSpaces]);
    useEffect(() => {
        const shouldPlayFirstSpace = selectedSpace.id == NULL_SPACE_ID && allSpacesOrdered.length > 0;
        if (shouldPlayFirstSpace) {
            if (defaultSpaceId == null) {
                playSpace(pickFirstSpaceToPlay(spacesById));
            } else {
                playSpace(spacesById[defaultSpaceId]);
            }
        }
    }, [allSpacesOrdered.length, defaultSpaceId, playSpace, selectedSpace, spacesById]);

    return {
        // State variables to be exposed
        activeCategories,
        spacesByCategory,
        featuredSpaces,
        recommendedSpaces,
        selectedTab,
        selectedSpace,
        selectedCategory,
        playerVolume: volume,
        savedSpaces,
        // Controller functions to be called
        getSpaceById,
        searchSpacesByText,
        toggleSpaceSaved,
        playSpace,
        selectCategory,
        playNextSpaceInCategory,
        playPreviousSpaceInCategory,
        setPlayerVolume: updateVolume,
        setActiveTab,
        isSpaceSaved,
    };
}

export type SpaceController = ReturnType<typeof useSpacePlayerController>;
